import { Modal, Timeline, Tag, Typography, Tooltip, Skeleton, Empty } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { loadTaskLogs } from './actions';
import { department, frequency, customerColumns } from './Utils';

var Text = Typography.Text;

var TimelineContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
`;

var TimelineItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

var TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

var ChangesList = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 12px;
`;

var ChangeItem = styled.div`
  color: #666;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

var ACTION_CONFIG = {
    created: { color: 'green', text: 'Criada' },
    updated: { color: 'blue', text: 'Atualizada' },
    deleted: { color: 'red', text: 'Excluída' }
};

var FIELD_LABELS = {
    name: 'Nome',
    department: 'Departamento',
    frequency: 'Periodicidade',
    description: 'Descrição',
    competence: 'Competência',
    first_date: '1º Vencimento',
    second_date: '2º Vencimento',
    third_date: '3º Vencimento',
    fourth_date: '4º Vencimento',
    custom_fields: 'Campos Extras',
    repeat_annually: 'Repetir Anualmente',
    file_upload: 'Upload de Documento',
    document_types: 'Tipos de Documento',
    deadline_control: 'Controle de Prazos',
    show_in_conference: 'Exibir na Conferência',
    category: 'Categoria',
    customer_columns: 'Colunas Adicionais'
};

var CATEGORY_LABELS = {
    taxes: 'Impostos',
    obligations: 'Obrigações',
    others: 'Outros'
};

var COMPETENCE_LABELS = {
    '0': 'Igual ao vencimento',
    '-1': '1 mês antes do vencimento',
    '-2': '2 meses antes do vencimento'
};

function formatValue(field, value) {
    if (value === null || value === undefined) return '-';

    if (field === 'department') {
        return department(value) || value;
    }
    if (field === 'frequency') {
        return frequency(value) || value;
    }
    if (field === 'category') {
        return CATEGORY_LABELS[value] || value;
    }
    if (field === 'competence') {
        return COMPETENCE_LABELS[String(value)] || value;
    }
    if (['first_date', 'second_date', 'third_date', 'fourth_date'].indexOf(field) !== -1) {
        var m = moment(value);
        return m.isValid() ? m.format('DD/MM/YYYY') : value;
    }
    if (field === 'file_upload' || field === 'deadline_control' || field === 'show_in_conference' || field === 'repeat_annually') {
        return value ? 'Sim' : 'Não';
    }
    if (field === 'custom_fields') {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            var fieldNames = Object.keys(value).map(function(key) {
                var entry = value[key];
                if (Array.isArray(entry) && entry[0]) return entry[0];
                return key;
            });
            return fieldNames.length > 0 ? fieldNames.join(', ') : '-';
        }
        return '-';
    }
    if (field === 'document_types') {
        if (Array.isArray(value) && value.length > 0) {
            var names = value.map(function(dt) { return dt.description || 'Sem nome'; });
            return names.join(', ');
        }
        return '-';
    }
    if (field === 'customer_columns') {
        if (Array.isArray(value) && value.length > 0) {
            var labels = value.map(function(col) { return customerColumns[col] || col; });
            return labels.join(', ');
        }
        return '-';
    }
    return String(value);
}

function renderChanges(changes) {
    if (!changes) return null;

    var before = changes.before || {};
    var after = changes.after || {};
    var fields = Object.keys(after);

    if (fields.length === 0) {
        fields = Object.keys(before);
    }

    if (fields.length === 0) return null;

    return (
        <ChangesList>
            {fields.map(function(field) {
                var label = FIELD_LABELS[field] || field;
                var oldValue = formatValue(field, before[field]);
                var newValue = formatValue(field, after[field]);

                return (
                    <ChangeItem key={field}>
                        <strong>{label}:</strong> {oldValue} &rarr; {newValue}
                    </ChangeItem>
                );
            })}
        </ChangesList>
    );
}

function TaskHistoryModal(props) {
    var isOpen = props.isOpen;
    var closeModal = props.closeModal;
    var taskId = props.taskId;

    var dispatch = useDispatch();
    var _useState = useState(false);
    var isLoading = _useState[0];
    var setIsLoading = _useState[1];
    var _useState2 = useState([]);
    var logs = _useState2[0];
    var setLogs = _useState2[1];

    useEffect(function() {
        if (isOpen && taskId) {
            setIsLoading(true);
            fetchLogs();
        }
    }, [isOpen, taskId]);

    function fetchLogs() {
        dispatch(loadTaskLogs(taskId))
            .then(function(response) {
                var data = response && response.payload && response.payload.data && response.payload.data.data;
                setLogs(data || []);
            })
            .catch(function(error) {
                console.error('Error loading task logs:', error);
                setLogs([]);
            })
            .finally(function() {
                setIsLoading(false);
            });
    }

    function formatTime(datetime) {
        moment.locale('pt-br');
        return moment(datetime).fromNow();
    }

    function formatFullDate(datetime) {
        moment.locale('pt-br');
        return moment(datetime).format('DD/MM/YYYY [às] HH:mm:ss');
    }

    function handleClose() {
        setLogs([]);
        closeModal();
    }

    return (
        <Modal
            title="Histórico de Alterações"
            visible={isOpen}
            onCancel={handleClose}
            footer={null}
            width={600}
            zIndex={1030}
        >
            {isLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : logs.length === 0 ? (
                <Empty description="Nenhum histórico encontrado" />
            ) : (
                <TimelineContainer>
                    <Timeline>
                        {logs.map(function(log) {
                            var config = ACTION_CONFIG[log.action] || { color: 'default', text: log.action };
                            var userName = log.user ? log.user.name : 'Sistema';

                            return (
                                <Timeline.Item key={log.id} color={config.color}>
                                    <TimelineItemContent>
                                        <TimelineHeader>
                                            <Tooltip title={formatFullDate(log.created_at)}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {formatTime(log.created_at)}
                                                </Text>
                                            </Tooltip>
                                            <Tag color={config.color}>
                                                {config.text}
                                            </Tag>
                                            <Text strong>
                                                {userName}
                                            </Text>
                                        </TimelineHeader>
                                        {log.description && (
                                            <div style={{ color: '#333', fontSize: 13 }}>
                                                {log.description}
                                            </div>
                                        )}
                                        {renderChanges(log.changes)}
                                    </TimelineItemContent>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                </TimelineContainer>
            )}
        </Modal>
    );
}

export default TaskHistoryModal;
